import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useHistory, useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";
import { ButtonNew as Button } from "components/buttonNew";
import { ClientPortalNavigation } from "../ClientPortalNavigation";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import { dequeue, enqueue, showError } from "redux/actions/ui";
import CustomDropdown from "components/customDropdown/CustomDropdown";
import { IFacility } from "redux/reducers/models/facility";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import CustomDropdownOption from "components/customDropdown/CustomDropdownOption";
import { MOBILE_WIDTH } from "helpers/ScreenSizes";
import { clientPortalUpdate } from "redux/actions/clientPortal";
import { UserActive } from "api/rpc";
import { GetClient } from "api/rpc/2024-04/customer/client";
import { GetClientGuest } from "api/rpc/2024-04/guest/client";
import { GetFacility } from "api/rpc/2024-04/customer/facility";
import { GetFacilityGuest } from "api/rpc/2024-04/guest/facility";
import { sortFacilities, IAccommodationReservation } from "redux/reducers/clientPortal";
import "./selectUnit.scss";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import GuestPicker from "components/guestPicker/GuestPicker";
import { Select } from "components/select/index";
import { faCircleCheck, faEnvelope, faStar, faXmark } from "@fortawesome/pro-regular-svg-icons";
import { IAccommodationUnitType } from "pages/secure/facility/accommodation/unitTypes/UnitTypes";
import { useAccommodationReservation } from "hooks/accommodation/useAccommodationReservation";
import { PostAccommodationCart } from "api/rpc/2024-04/customer/accommodation/reservations/reservations";
import { useClientPortal } from "../useClientPortal";
import AccommodationUnitCard from "components/accommodation/unitCard/AccommodationUnitCard";

interface IParams {
  clientShortName: string;
  facilityShortName: string;
}
interface IFilterState {
  facilitySearchQuery: string;
  searchedFacilities: Array<IFacility>;
  searchQuery: string;
  showSearch: boolean;
  filter: string;
}

export default function SelectUnit() {
  const { t } = useTranslation();
  const { clientShortName, facilityShortName } = useParams<IParams>();
  const history = useHistory();

  const { clientPortalStore } = useAppSelector(store => store);
  const windowSize = useWindowSize();
  const dispatch = useAppDispatch();

  const {
    mergedUnitTypes,
    accommodationCategories,
    rooms,
    newReservationState,
    setRooms,
    setNewReservationState,
    handleCloseConfirmation: closeConfirmation,
  } = useAccommodationReservation({
    isAdmin: false,
    facilityId: clientPortalStore?.selectedFacility?.id,
  });

  const [filterState, setFilterState] = useState<IFilterState>({
    facilitySearchQuery: "",
    searchedFacilities: [],
    searchQuery: "",
    showSearch: false,
    filter: "",
  });

  const { getHomePageUrl } = useClientPortal();

  useEffect(() => {
    if (clientPortalStore?.selectedFacility && !clientPortalStore?.selectedFacility?.module_accommodations) {
      history.push(getHomePageUrl());
    }
  }, [clientPortalStore?.selectedFacility, history]);

  const [filteredUnitTypes, setFilteredUnitTypes] = useState<IAccommodationUnitType[]>([]);

  useEffect(() => {
    if (clientPortalStore.client === null) {
      void getClient();
    }
  }, []);

  useEffect(() => {
    if (mergedUnitTypes.length > 0) {
      let sorted = [...mergedUnitTypes];

      // Filter by occupancy and availability
      const totalGuests = rooms.reduce((acc, room) => acc + room.adults + room.children, 0);
      sorted = sorted.filter(unit => {
        if (totalGuests > unit.max_occupancy) {
          return false;
        }
        if (totalGuests < unit.base_occupancy) {
          return false;
        }

        // Exclude unit types with no price and no price plan IDs
        if (!(unit.price || 0) && (!unit.planPriceIds || unit.planPriceIds.length === 0)) {
          return false;
        }

        return true;
      });

      if (filterState.filter === "lowToHigh") {
        sorted.sort((a, b) => (a.price || 0) - (b.price || 0));
      } else if (filterState.filter === "highToLow") {
        sorted.sort((a, b) => (b.price || 0) - (a.price || 0));
      }
      setFilteredUnitTypes(sorted);
    }
  }, [filterState.filter, mergedUnitTypes, rooms]);

  const selectedFacilityElement = (
    <div className={"accommodations-select-unit-facility-container"}>
      {clientPortalStore?.client?.facilities?.length > 1 && clientPortalStore?.selectedFacility?.logo_source && (
        <div>
          <img
            className="accommodations-select-unit-home-course-logo"
            src={clientPortalStore?.selectedFacility?.logo_source}
            alt="Facility Logo"
          />
        </div>
      )}
      <div>
        <div className="accommodations-select-unit-home-course-name">
          {clientPortalStore?.selectedFacility?.long_name}
        </div>
        {clientPortalStore?.selectedFacility && (
          <div className="accommodations-select-unit-secondary-information">
            {`${clientPortalStore?.selectedFacility?.address_line_1}, ${clientPortalStore?.selectedFacility?.city}, ${clientPortalStore?.selectedFacility?.province_name}, ${clientPortalStore?.selectedFacility?.postal}`}
          </div>
        )}
      </div>
    </div>
  );

  // const facilities = useMemo(() => {
  //   if (filterState?.facilitySearchQuery) {
  //     return clientPortalStore?.client?.facilities?.filter(
  //       facility =>
  //         facility?.long_name.toLowerCase().includes(filterState?.facilitySearchQuery?.toLowerCase()) ||
  //         facility?.city.toLowerCase().includes(filterState?.facilitySearchQuery?.toLowerCase()),
  //     );
  //   } else if (clientPortalStore?.client?.facilities) {
  //     return clientPortalStore?.client?.facilities;
  //   } else {
  //     return [];
  //   }
  // }, [clientPortalStore?.client?.facilities, filterState.facilitySearchQuery]);

  async function getClient() {
    let currentActiveUser = !!clientPortalStore?.user;
    let selectedClient = clientPortalStore?.client;
    let selectedFacility = clientPortalStore?.selectedFacility;

    dispatch(enqueue());

    const activeUserRes = await UserActive(false);
    if (activeUserRes?.status === StatusCode.OK) {
      currentActiveUser = true;
    }

    if (Number(activeUserRes?.data?.user_type) === 2 || Number(activeUserRes?.data?.user_type) === 3) {
      dispatch(dequeue());
      return;
    }

    if (
      !clientPortalStore?.client ||
      !clientPortalStore?.selectedFacility ||
      (clientPortalStore?.selectedFacility?.favourite === undefined && currentActiveUser)
    ) {
      let clientRes;
      let facilityRes;
      if (currentActiveUser) {
        clientRes = await GetClient({ short_name: clientShortName }, true);
      } else {
        clientRes = await GetClientGuest({ short_name: clientShortName }, true);
      }

      if (clientRes.status !== StatusCode.OK || clientRes?.data?.length === 0) {
        dispatch(dequeue());
        dispatch(showError(t("Error loading client")));
        return;
      }
      selectedClient = clientRes?.data[0];
      if (currentActiveUser) {
        facilityRes = await GetFacility({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      } else {
        facilityRes = await GetFacilityGuest({ client_id: selectedClient?.id, order_by: "long_name" }, false);
      }

      if (facilityRes?.status !== StatusCode.OK || facilityRes?.data?.length === 0) {
        dispatch(dequeue());
        dispatch(showError(t("Error loading facilities")));
        return;
      }

      const sortedFacilities = sortFacilities(facilityRes?.data);

      selectedFacility = selectedFacility
        ? sortedFacilities?.find(facility => facility?.id === selectedFacility?.id)
        : facilityShortName
        ? sortedFacilities?.find(facility => facility?.short_name === facilityShortName)
        : sortedFacilities[0];

      if (!selectedFacility && sortedFacilities?.length > 0) {
        selectedFacility = sortedFacilities[0];
      }
      selectedClient = { ...selectedClient, facilities: sortedFacilities };

      dispatch(
        clientPortalUpdate({
          client: selectedClient,
          selectedFacility,
        }),
      );
    }

    ReactDOM.unstable_batchedUpdates(() => {
      dispatch(dequeue());
      dispatch(
        clientPortalUpdate({
          user: currentActiveUser ? activeUserRes?.data : undefined,
        }),
      );
    });
  }

  function handleFacilityChange(id: number) {
    const foundFacility = clientPortalStore?.client?.facilities?.find(facility => facility?.id === id);
    if (foundFacility) {
      ReactDOM.unstable_batchedUpdates(() => {
        dispatch(clientPortalUpdate({ selectedFacility: foundFacility }));
      });
    }
  }

  function handleFacilitySearch(query: string) {
    setFilterState(prevState => ({ ...prevState, facilitySearchQuery: query }));
  }

  async function handleUnitTypeSelect(
    unitType: IAccommodationUnitType & { price: number; planPriceIds?: number[]; isSoldOut?: boolean },
  ) {
    if (unitType.isSoldOut) {
      return;
    }
    if (!clientPortalStore?.user) {
      const loginAddress = `/portal/${clientShortName}/login?facility=${clientPortalStore?.selectedFacility?.short_name}`;
      history.push(loginAddress);
      return;
    }

    const planPriceIds = unitType.planPriceIds ?? [];
    if (planPriceIds.length === 0 || !clientPortalStore?.selectedFacility?.id) {
      dispatch(showError("Unable to price this stay"));
      return;
    }

    const cartRes = await PostAccommodationCart(
      {
        facility_id: clientPortalStore.selectedFacility.id,
        nightly_rates: planPriceIds,
      },
      true,
    );
    const quote = cartRes.data?.data;
    if (cartRes.status !== StatusCode.OK || !quote?.cart_token) {
      dispatch(showError(String(cartRes.data?.message) || "Unable to price this stay"));
      return;
    }

    const adults = rooms?.reduce((acc, room) => acc + room.adults, 0);
    const children = rooms?.reduce((acc, room) => acc + room.children, 0);
    const totalRooms = rooms.length;
    const duration = newReservationState.departure_date.getTime() - newReservationState.arrival_date.getTime();
    const nights = Math.ceil(duration / (1000 * 60 * 60 * 24));

    const accommodationData: IAccommodationReservation = {
      category_id: unitType.category_id,
      unit_type_id: unitType.id,
      nightly_rates: newReservationState.nightly_rates,
      arrival_date: newReservationState.arrival_date,
      departure_date: newReservationState.departure_date,
      currency: newReservationState.currency,
      subtotalPrice: quote.subtotal_price,
      totalPrice: quote.total_price,
      taxAmount: quote.total_tax,
      cart_token: quote.cart_token,
      selectedUnitType: unitType,
      nights: nights,
      adults: adults,
      children: children,
      rooms: totalRooms,
    };

    dispatch(clientPortalUpdate({ accommodationReservation: accommodationData }));
    history.push(
      `/portal/${clientShortName}/accommodations/${clientPortalStore?.selectedFacility?.short_name ?? ""}/reserve`,
    );
  }

  return (
    <div>
      <ClientPortalNavigation showNavOptions />

      <div className="accommodations-select-unit-dropdown-facility-container">
        {clientPortalStore?.client?.facilities?.length > 1 ? (
          <CustomDropdown
            parentElement={selectedFacilityElement}
            onChange={handleFacilityChange}
            defaultValue={clientPortalStore?.selectedFacility?.id}
            onSearch={handleFacilitySearch}
            searchPlacerHolder="Search courses..."
          >
            {clientPortalStore?.client?.facilities?.map((facility, index) => (
              <CustomDropdownOption value={facility.id} key={index}>
                <div className="accommodations-select-unit-facility-option-container">
                  <img
                    className="accommodations-select-unit-facility-option-facility-logo"
                    src={facility?.logo_source}
                    alt="Facility Logo"
                  />
                  <p className="accommodations-select-unit-facility-option-facility-name">{facility?.long_name}</p>
                  {windowSize.width > MOBILE_WIDTH ? (
                    <p className="accommodations-select-unit-facility-option-facility-address">{facility?.city}</p>
                  ) : null}
                  {facility?.favourite && (
                    <FontAwesomeIcon
                      size="1x"
                      fixedWidth
                      className="accommodations-select-unit-facility-option-favourite"
                      icon={faStar}
                    />
                  )}
                </div>
              </CustomDropdownOption>
            ))}
          </CustomDropdown>
        ) : (
          selectedFacilityElement
        )}

        <div>
          <Button type="secondary" size="small" onClick={() => window.open(`/tee-on/portal/loyalty-program`, "_blank")}>
            <FontAwesomeIcon icon={faStar} />
            <p className="ml-2">{t("Join loyalty program")}</p>
          </Button>
        </div>
      </div>

      <div className="accommodations-select-unit-header">
        <p className="accommodations-select-unit-header_title">{t("Select a unit")}</p>
        <p className="accommodations-select-unit-header_description">
          {t("Set your length of stay, number of guests for your reservation.")}
        </p>
        <hr />

        <div className="flex justify-between items-center mt-2 mb-5">
          <div className="flex gap-6 items-center">
            <DatePickerInput
              months={1}
              position="left"
              showQuickOptions
              startingDate={newReservationState?.arrival_date}
              setStartingDate={(date: Date) => setNewReservationState(p => ({ ...p, arrival_date: date }))}
              endingDate={newReservationState?.departure_date}
              setEndingDate={(date: Date) => setNewReservationState(p => ({ ...p, departure_date: date }))}
            />
            <div className="accommodations-select-unit-guest-picker-container">
              <GuestPicker rooms={rooms} onRoomsChange={setRooms} hideLabel />
            </div>
          </div>

          <div>
            <Select
              onChange={value => setFilterState(prev => ({ ...prev, filter: value as string }))}
              placeholder="Sort by"
              showPlaceholder
              className="w-sm"
              allowClear
              handleClear={() => setFilterState(prevState => ({ ...prevState, filter: "" }))}
            >
              <Select.Option value={"lowToHigh"} name={"Price: Low to High"}>
                {t("Price: Low to High")}
              </Select.Option>
              <Select.Option value={"highToLow"} name={"Price: High to Low"}>
                {t("Price: High to Low")}
              </Select.Option>
            </Select>
          </div>
        </div>


          {filteredUnitTypes.length > 0 ? (
            <>
              {filteredUnitTypes?.map(
                (
                  unitType: IAccommodationUnitType & { price: number; planPriceIds?: number[]; isSoldOut?: boolean },
                  index: number,
                ) => (
                  <div key={index} className="mt-2 mb-4 cursor-pointer" onClick={() => handleUnitTypeSelect(unitType)}>
                    <AccommodationUnitCard
                      accommodationType={
                        accommodationCategories?.find(category => category.id === unitType?.category_id)?.title
                      }
                      title={unitType?.title}
                      // bedDetails="1 Bed"
                      capacity={unitType?.max_occupancy + " people"}
                      amenities={unitType?.amenities}
                      price={unitType?.price}
                      isSoldOut={unitType?.isSoldOut}
                      // nextAvailableDate={unitType?.nextAvailableDate}
                    />
                  </div>
                ),
              )}
            </>
          ) : (
            <div className="accommodations-select-unit-no-results">
              <div className="accommodations-select-unit-no-results_content">
                <p className="accommodations-select-unit-no-results_title">{t("No units available")}</p>
                <p className="accommodations-select-unit-no-results_description">
                  {t(
                    "We couldn't find any available units for the selected dates and guest count. Please try adjusting your search.",
                  )}
                </p>
              </div>
            </div>
          )}

      </div>

      {clientPortalStore?.accommodationReservation?.confirmationNumber && (
        <div className="reservations-confirmed-overlay" onClick={closeConfirmation}>
          <div className="reservations-confirmed-content" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="reservations-confirmed-content_success_icon">
                <FontAwesomeIcon icon={faCircleCheck} />
              </div>
              <FontAwesomeIcon className="cursor-pointer" icon={faXmark} onClick={closeConfirmation} />
            </div>

            <div className="mb-3">
              <p className="reservations-confirmed-content_title mt-2">{t("Reservation confirmed")}</p>
              <p className="reservations-confirmed-content_subtitle mt-2">
                {t("Success! Reservation")} #{clientPortalStore.accommodationReservation.confirmationNumber}{" "}
                {t("has been successfully booked.")}
              </p>
            </div>

            <div className="reservations-confirmed-content_section_container">
              <p className="reservations-confirmed-content_section_title">{t("Reservation info")}</p>
              <div className="reservations-confirmed-content_section_item_container">
                <p className="reservations-confirmed-content_section_item_title">{t("Check-in")}</p>
                <p className="reservations-confirmed-content_section_item_content">
                  {new Date(clientPortalStore.accommodationReservation.arrival_date).toLocaleDateString()}
                </p>
              </div>
              <div className="reservations-confirmed-content_section_item_container">
                <p className="reservations-confirmed-content_section_item_title">{t("Check-out")}</p>
                <p className="reservations-confirmed-content_section_item_content">
                  {new Date(clientPortalStore.accommodationReservation.departure_date).toLocaleDateString()}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-between mt-4 mb-2">
              <Button className="w-full justify-center" type="secondary">
                <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                {t("Email confirmation")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
